import React from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import { StatusPill } from '../ui/YoolTable';
import {
  trazabilidadDeOrden,
  rutaContratacion,
  rutaSuscripcion,
  rutaCertificado,
} from '../../lib/ordenesPago/trazabilidad';
import { instanteDeDb } from '../../lib/fechas';

/**
 * Un código de la cadena (CT-…, OP-…, PY-…, SU-…, CE-…). Si tiene pantalla donde
 * mirarlo, es un enlace; si no —el pago no tiene lista propia— es texto plano.
 */
export function CodigoTraza({ codigo, to, titulo }) {
  if (!codigo) return <span className="text-xs text-muted-foreground">—</span>;

  const texto = <span className="font-mono text-xs">{codigo}</span>;

  if (!to) return <span title={titulo}>{texto}</span>;

  return (
    <Link
      to={to}
      title={titulo ? `${titulo} · abrir en su pantalla` : 'Abrir en su pantalla'}
      className="inline-flex items-center gap-1 text-[var(--yool-primary,#2563eb)] hover:underline"
    >
      {texto}
      <ExternalLink className="h-3 w-3 opacity-60" />
    </Link>
  );
}

/**
 * La trazabilidad comprimida para una fila de la lista: la suscripción a la que
 * pertenece el pago y el certificado que se emitió. Cuando hay más de un
 * certificado (varios productos, o titular y carga) se muestra el primero y "+N".
 *
 * Si faltan certificados para las contrataciones de la orden, la fila lo dice: es
 * lo único que permite ver el hueco sin abrir el detalle de cada orden.
 */
export function CeldaTrazabilidad({ orden }) {
  const { suscripcion, certificados, contrataciones, faltanCertificados } =
    trazabilidadDeOrden(orden);
  const [primero, ...resto] = certificados;

  if (!suscripcion && !primero) {
    return <span className="text-xs text-muted-foreground">Sin emitir</span>;
  }

  return (
    <div className="leading-tight space-y-0.5">
      <div>
        <CodigoTraza
          codigo={suscripcion?.codigo}
          to={suscripcion?.codigo ? rutaSuscripcion(suscripcion.codigo) : null}
          titulo="Suscripción"
        />
      </div>
      <div className="flex items-center gap-1">
        <CodigoTraza
          codigo={primero?.codigo}
          to={primero?.codigo ? rutaCertificado(primero.codigo) : null}
          titulo="Certificado"
        />
        {resto.length > 0 && (
          <span className="text-[11px] text-muted-foreground" title={resto.map((c) => c.codigo).join(', ')}>
            +{resto.length}
          </span>
        )}
        {faltanCertificados > 0 && (
          <span
            className="text-[11px] text-[var(--badge-warning-fg,#92400e)]"
            title={`La orden está pagada y cubre ${contrataciones.length} contrataciones, pero solo tiene ${certificados.length} certificado(s)`}
          >
            faltan {faltanCertificados}
          </span>
        )}
      </div>
    </div>
  );
}

/** Una fila de la tarjeta de detalle: etiqueta, código enlazado, estado y fecha. */
function Eslabon({ etiqueta, codigo, to, estado, fecha, vacio }) {
  return (
    <div className="grid grid-cols-[7.5rem_1fr_auto] items-center gap-3 py-2">
      <span className="text-sm text-muted-foreground">{etiqueta}</span>
      <div className="min-w-0">
        {codigo ? (
          <CodigoTraza codigo={codigo} to={to} titulo={etiqueta} />
        ) : (
          <span className="text-xs text-muted-foreground italic">{vacio}</span>
        )}
      </div>
      <div className="flex items-center gap-2 justify-end">
        {estado && <StatusPill estado={estado} tone="ring" />}
        {fecha && <span className="text-xs text-muted-foreground whitespace-nowrap">{fecha}</span>}
      </div>
    </div>
  );
}

/**
 * La cadena completa de una orden, de arriba abajo: de qué contratación nació,
 * la orden misma, qué pago la cerró, qué suscripción quedó viva y qué
 * certificados se emitieron. Los eslabones que aún no existen se muestran
 * igual, en gris, para que se vea dónde se cortó el flujo.
 */
export function TarjetaTrazabilidad({ orden, formatearFecha }) {
  const { contrataciones, pago, suscripcion, certificados, certificadoDe, faltanCertificados } =
    trazabilidadDeOrden(orden);

  // Los códigos de las contrataciones, para decir de cuál es cada certificado.
  const codigoContratacion = Object.fromEntries(
    contrataciones.map((c) => [c.id, c.codigo])
  );

  // El instante se normaliza ACÁ, no en el formateador. Esta tarjeta mezcla
  // columnas con zona (`ordenes_pago.created_at`, `certificados.fecha_emision`)
  // con columnas sin zona (`pagos.transaction_date`), y solo quien sabe de qué
  // columna viene cada valor puede arreglarlo: un formateador recibe un string
  // suelto y no tiene cómo saberlo. Sin esto el pago se leía 4 horas tarde y la
  // cadena salía desordenada —el certificado antes del pago que lo originó—.
  const fecha = (valor) => (valor ? formatearFecha(instanteDeDb(valor)) : null);

  return (
    <div className="divide-y">
      {/* Un carrito de N productos cuelga de esta orden: se listan las N, no solo
          la primaria de `ordenes_pago.contratacion_id`. Con una sola es una lista
          de un elemento y se ve igual que antes. */}
      {contrataciones.length <= 1 ? (
        <Eslabon
          etiqueta="Contratación"
          codigo={contrataciones[0]?.codigo}
          to={contrataciones[0]?.codigo ? rutaContratacion(contrataciones[0].codigo) : null}
          estado={contrataciones[0]?.estado}
          vacio="Sin contratación asociada"
        />
      ) : (
        <div className="grid grid-cols-[7.5rem_1fr] items-start gap-3 py-2">
          <span className="text-sm text-muted-foreground">
            Contrataciones ({contrataciones.length})
          </span>
          <div className="space-y-1.5">
            {contrataciones.map((c) => (
              <div key={c.id} className="flex items-center justify-between gap-3">
                <CodigoTraza
                  codigo={c.codigo}
                  to={rutaContratacion(c.codigo)}
                  titulo="Contratación"
                />
                {c.estado && <StatusPill estado={c.estado} tone="ring" />}
              </div>
            ))}
          </div>
        </div>
      )}
      <Eslabon
        etiqueta="Orden"
        codigo={orden?.codigo}
        estado={orden?.estado}
        fecha={fecha(orden?.created_at)}
        vacio="Sin código"
      />
      <Eslabon
        etiqueta="Pago"
        codigo={pago?.codigo}
        estado={pago?.status}
        fecha={fecha(pago?.transaction_date)}
        vacio="La orden todavía no se ha pagado"
      />
      <Eslabon
        etiqueta="Suscripción"
        codigo={suscripcion?.codigo}
        to={suscripcion?.codigo ? rutaSuscripcion(suscripcion.codigo) : null}
        estado={suscripcion?.status}
        fecha={fecha(suscripcion?.start_date)}
        vacio="No se creó suscripción"
      />

      <div className="grid grid-cols-[7.5rem_1fr] items-start gap-3 py-2">
        <span className="text-sm text-muted-foreground">
          {certificados.length > 1
            ? `Certificados (${certificados.length} de ${contrataciones.length})`
            : 'Certificado'}
        </span>
        {certificados.length === 0 ? (
          <span className="text-xs text-muted-foreground italic">Todavía no se emite certificado</span>
        ) : (
          <div className="space-y-1.5">
            {/* Una orden pagada debe terminar con un certificado por contratación.
                Que falte alguno es el defecto que había que descubrir consultando
                la base a mano; acá se ve de entrada. */}
            {faltanCertificados > 0 && (
              <div className="text-[11px] text-[var(--badge-warning-fg,#92400e)]">
                Faltan {faltanCertificados} de {contrataciones.length} certificados
              </div>
            )}
            {certificados.map((cert) => {
              // De qué contrato del carrito es este certificado. Con varias
              // contrataciones hay que decirlo: sin esto el certificado se leía
              // como si fuera del único contrato visible, que era el equivocado.
              const deCodigo = codigoContratacion[certificadoDe[cert.id]];
              return (
                <div key={cert.id} className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <CodigoTraza codigo={cert.codigo} to={rutaCertificado(cert.codigo)} titulo="Certificado" />
                    {contrataciones.length > 1 && (
                      <div className="text-[11px] text-muted-foreground">
                        {deCodigo ? `de ${deCodigo}` : 'contrato sin identificar'}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {cert.estado && <StatusPill estado={cert.estado} tone="ring" />}
                    {cert.fecha_emision && (
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {fecha(cert.fecha_emision)}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
